/**
 * RateLimitPreview - Lite Deterministic Preview for Rate-Limited Users
 * 
 * Shows key FIRE metrics and tax savings estimates when users can't generate
 * a full AI journey (rate limited). Uses pre-computed deterministic data.
 * 
 * Purpose: Don't leave users empty-handed when rate limited.
 */

import { TrendingUp, Clock, Wallet, Shield, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { PreComputedJourneyData, FireSnapshot } from '@/lib/ai/schemas/factPack.schema';

interface RateLimitPreviewProps {
  preComputedData: PreComputedJourneyData | null;
  fireSnapshot?: FireSnapshot | null;
  onGoToDashboard: () => void;
}

// Format currency in lakhs/crores
function formatINR(amount: number): string {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)}Cr`;
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  }
  return `₹${amount.toLocaleString('en-IN')}`;
}

export function RateLimitPreview({ preComputedData, fireSnapshot, onGoToDashboard }: RateLimitPreviewProps) {
  if (!preComputedData && !fireSnapshot) {
    return null;
  }

  const taxSavings = preComputedData?.taxSavings;
  const heroStats = preComputedData?.heroStats;
  const socialSecurity = preComputedData?.socialSecurity;

  // Check if we have meaningful data to show
  const hasData = heroStats?.rnorEligible || 
                  (taxSavings?.total && taxSavings.total > 0) ||
                  fireSnapshot?.monthsToFIRE !== undefined;

  if (!hasData) {
    return null;
  }

  return (
    <div className="mt-6 space-y-4 w-full max-w-md">
      <div className="text-center">
        <p className="text-sm font-medium text-slate-600 mb-1">
          While you wait, here's a preview based on your data:
        </p>
      </div>

      <Card className="bg-gradient-to-br from-slate-50 to-blue-50 border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-700">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            Your FIRE Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* FIRE Timeline */}
            {fireSnapshot && (
              <div className="bg-white rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <Clock className="w-3 h-3" />
                  Time to FIRE
                </div>
                <div className="font-semibold text-slate-800">
                  {fireSnapshot.fireStatus === 'achieved' 
                    ? '✅ Achieved!' 
                    : fireSnapshot.monthsToFIRE > 0
                      ? `${Math.round(fireSnapshot.monthsToFIRE / 12)} years`
                      : 'Calculating...'}
                </div>
                <div className={`text-xs ${
                  fireSnapshot.fireStatus === 'achieved' ? 'text-emerald-600' :
                  fireSnapshot.fireStatus === 'on_track' ? 'text-blue-600' : 'text-amber-600'
                }`}>
                  {fireSnapshot.fireStatus === 'achieved' ? 'Financial Independence' :
                   fireSnapshot.fireStatus === 'on_track' ? 'On Track' : 'Needs Attention'}
                </div>
              </div>
            )}

            {/* Tax Savings */}
            {taxSavings && taxSavings.total > 0 && (
              <div className="bg-white rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <Wallet className="w-3 h-3" />
                  Est. Tax Savings
                </div>
                <div className="font-semibold text-emerald-700">
                  {taxSavings.totalFormatted || formatINR(taxSavings.total)}
                </div>
                <div className="text-xs text-slate-500">
                  with RNOR optimization
                </div>
              </div>
            )}

            {/* RNOR Status */}
            {heroStats && (
              <div className="bg-white rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <Shield className="w-3 h-3" />
                  RNOR Status
                </div>
                <div className="font-semibold text-slate-800">
                  {heroStats.rnorEligible ? `${heroStats.rnorYears} years` : 'Not Eligible'}
                </div>
                <div className="text-xs text-slate-500">
                  {heroStats.rnorEligible ? 'Tax-free foreign income' : 'Based on residency'}
                </div>
              </div>
            )}

            {/* Social Security */}
            {socialSecurity && (
              <div className="bg-white rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <TrendingUp className="w-3 h-3" />
                  US Social Security
                </div>
                <div className="font-semibold text-slate-800">
                  {socialSecurity.eligible 
                    ? '✅ Eligible' 
                    : socialSecurity.yearsToQualify > 0
                      ? `${socialSecurity.yearsToQualify} yrs to qualify`
                      : 'Check Dashboard'}
                </div>
                <div className="text-xs text-slate-500">
                  {socialSecurity.eligible 
                    ? `${socialSecurity.creditsEarned} credits earned` 
                    : `${socialSecurity.creditsEarned}/${socialSecurity.creditsNeeded} credits`}
                </div>
              </div>
            )}
          </div>

          {/* CTA to Dashboard */}
          <div className="pt-2 border-t border-slate-200">
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={onGoToDashboard}
            >
              <span>Explore Full Dashboard</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <p className="text-xs text-center text-slate-500 mt-2">
              View detailed projections, tax analysis, and 7 interactive tabs
            </p>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-center text-slate-400">
        These are deterministic estimates. The full AI journey provides personalized action items and scenarios.
      </p>
    </div>
  );
}

export default RateLimitPreview;
